import {
  AddCircleOutlineRounded as AddCircleOutlineRoundedIcon,
  AutorenewRounded as AutorenewRoundedIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  DragHandleOutlined as DragHandleOutlinedIcon,
  HideImageOutlined as HideImageOutlinedIcon,
  ImageOutlined as ImageOutlinedIcon,
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
import {
  Accordion,
  ACCORDION_VARIANT,
  AccordionItem,
  ActionMenu,
  theme as dsTheme,
  Tooltip,
} from '@sb/cc-design-system';
import { groupBy, pick } from 'lodash';
import { MouseEvent, useState } from 'react';
import { ProductListModalV2 } from 'src/components/modal/productListModalV2/ProductListModalV2';
import { IProduct } from 'src/interfaces';
import { ComponentTypes } from 'src/interfaces/ComponentTypesList';
import { lang } from 'src/utils';
import { getPlainText } from 'src/utils/util-functions';

import { DEFAULT_ACCORDION_STYLE } from '../../constants';
import {
  getProductLineupAccordionsData,
  getProductListModalFilters,
} from './ProductLineupAccordion.helper';
import {
  IProductLineupAccordion,
  IProductLineupAccordionData,
} from './ProductLineupAccordion.interface';
import {
  AccordionItemActions,
  AccordionItemContent,
  AccordionItemLabel,
  ProductLineupAccordionWrapper,
} from './ProductLineupAccordion.styled';

const CategoryAccordion = ({
  overrideId,
  selectProductKey,
  accordion,
  isSubAccordion = false,
  onSelectAccordionProduct,
  onAddProductClick,
  onReplaceAccordionProduct,
  onOpenReplaceModal,
  onReorderAccordionProduct,
  onRemoveAccordionProduct,
}: Pick<
  IProductLineupAccordion,
  | 'overrideId'
  | 'selectProductKey'
  | 'onSelectAccordionProduct'
  | 'onReplaceAccordionProduct'
  | 'onReorderAccordionProduct'
  | 'onRemoveAccordionProduct'
> & {
  accordion: IProductLineupAccordionData;
  isSubAccordion?: boolean;
  onAddProductClick: (
    event: MouseEvent<HTMLButtonElement>,
    accordion: IProductLineupAccordionData,
  ) => void;
  onOpenReplaceModal: (
    item: IProductLineupAccordionData['items'][number],
  ) => void;
}) => {
  return (
    <Accordion
      key={accordion.key}
      title={accordion.title}
      variant={ACCORDION_VARIANT.leftArrow}
      activeItemKey={selectProductKey}
      showBorder
      showBorderRadius
      sortable
      onSortEnd={(oldIndex, newIndex) =>
        overrideId !== undefined &&
        onReorderAccordionProduct?.(
          overrideId,
          accordion.key,
          oldIndex,
          newIndex,
        )
      }
      rightIcon={
        <IconButton
          aria-label={`${lang.addProduct} to ${getPlainText(
            accordion.title ?? '',
          )}`}
          size="small"
          onClick={(event) => onAddProductClick(event, accordion)}>
          <AddCircleOutlineRoundedIcon fontSize="small" />
        </IconButton>
      }
      summaryProps={{
        sx: {
          ...(isSubAccordion && { pl: 7 }),
        },
      }}
      {...DEFAULT_ACCORDION_STYLE}>
      {accordion.items.map((item) => {
        const label = getPlainText(item.label);
        const ImageIcon = item.isImageVisible
          ? ImageOutlinedIcon
          : HideImageOutlinedIcon;
        const canRemoveProduct = accordion.items.length > 1;
        const menuItems = [
          ...(onReplaceAccordionProduct
            ? [
                {
                  value: 'replace',
                  label: lang.scheduleComponent.preview.replace,
                  icon: <AutorenewRoundedIcon fontSize="small" />,
                  onClick: () => onOpenReplaceModal(item),
                },
              ]
            : []),
          ...(onRemoveAccordionProduct
            ? [
                {
                  value: 'remove',
                  label: lang.remove,
                  icon: <DeleteOutlineOutlinedIcon fontSize="small" />,
                  disabled: !canRemoveProduct,
                  onClick: () => {
                    if (overrideId !== undefined && canRemoveProduct) {
                      onRemoveAccordionProduct(overrideId, item.key);
                    }
                  },
                },
              ]
            : []),
        ];

        return (
          <AccordionItem
            key={item.key}
            active={selectProductKey === item.key}
            {...(isSubAccordion && { style: { paddingLeft: '28px' } })}
            label={
              <AccordionItemContent>
                <ImageIcon
                  fontSize="small"
                  sx={{ ml: 2, color: dsTheme.palette.text.secondary }}
                />
                <Tooltip showOnOverflowOnly title={label}>
                  <AccordionItemLabel variant="body2">
                    {label}
                  </AccordionItemLabel>
                </Tooltip>
                {overrideId !== undefined && menuItems.length > 0 && (
                  <AccordionItemActions
                    onClick={(event: MouseEvent<HTMLDivElement>) =>
                      event.stopPropagation()
                    }>
                    <ActionMenu
                      items={menuItems}
                      triggerAriaLabel={`Open actions for ${label}`}
                      menuWidth={136}
                      showItemIcon
                    />
                  </AccordionItemActions>
                )}
              </AccordionItemContent>
            }
            leftIcon={<DragHandleOutlinedIcon fontSize="small" />}
            onClick={() => onSelectAccordionProduct?.(item.key)}
            {...pick(DEFAULT_ACCORDION_STYLE, 'height')}
          />
        );
      })}
    </Accordion>
  );
};

export const ProductLineupAccordion = ({
  overrideId,
  component,
  products,
  selectProductKey,
  onSelectAccordionProduct,
  onReorderAccordionProduct,
  onAddAccordionProducts,
  onReplaceAccordionProduct,
  onRemoveAccordionProduct,
}: IProductLineupAccordion) => {
  const [selectedAddGroup, setSelectedAddGroup] =
    useState<IProductLineupAccordionData>();
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
  const [selectedReplaceItem, setSelectedReplaceItem] =
    useState<IProductLineupAccordionData['items'][number]>();
  const accordions = getProductLineupAccordionsData(component, products);
  const productListModalFilters = getProductListModalFilters(
    component?.product_category_id,
    selectedReplaceItem?.productCode,
  );

  const handleOpenAddProductModal = (
    event: MouseEvent<HTMLButtonElement>,
    accordion: IProductLineupAccordionData,
  ) => {
    event.stopPropagation();
    setSelectedAddGroup(accordion);
    setIsAddProductModalOpen(true);
  };

  const handleCloseAddProductModal = () => {
    setIsAddProductModalOpen(false);
    setSelectedAddGroup(undefined);
  };

  const handleAddProductSelect = (selectedProducts: IProduct[]) => {
    if (overrideId !== undefined && selectedAddGroup?.key) {
      onAddAccordionProducts?.(
        overrideId,
        selectedAddGroup.key,
        selectedProducts,
      );
    }

    handleCloseAddProductModal();
  };

  const handleOpenReplaceModal = (
    item: IProductLineupAccordionData['items'][number],
  ) => {
    setSelectedReplaceItem(item);
    setIsReplaceModalOpen(true);
  };

  const handleCloseReplaceModal = () => {
    setIsReplaceModalOpen(false);
    setSelectedReplaceItem(undefined);
  };

  const handleReplaceProductSelect = (selectedProducts: IProduct[]) => {
    const replacementProduct = selectedProducts[0];

    if (
      selectedReplaceItem?.key &&
      replacementProduct &&
      overrideId !== undefined
    ) {
      onReplaceAccordionProduct?.(
        overrideId,
        selectedReplaceItem.key,
        replacementProduct,
      );
    }

    handleCloseReplaceModal();
  };

  return (
    <ProductLineupAccordionWrapper>
      {component?.type === ComponentTypes.LISTING_VISUALS_GALLERY ? (
        Object.entries(
          groupBy(accordions, ({ columnOrder = 1 }) => columnOrder),
        ).map(([columnOrder, groupedAccordions]) => (
          <Accordion
            key={columnOrder}
            title={`${lang.column} ${columnOrder}`}
            variant={ACCORDION_VARIANT.leftArrow}
            showBorder
            showBorderRadius={false}
            {...DEFAULT_ACCORDION_STYLE}>
            {groupedAccordions.map((accordion) => (
              <CategoryAccordion
                key={accordion.key}
                overrideId={overrideId}
                selectProductKey={selectProductKey}
                accordion={accordion}
                isSubAccordion
                onSelectAccordionProduct={onSelectAccordionProduct}
                onAddProductClick={handleOpenAddProductModal}
                onReplaceAccordionProduct={onReplaceAccordionProduct}
                onOpenReplaceModal={handleOpenReplaceModal}
                onReorderAccordionProduct={onReorderAccordionProduct}
                onRemoveAccordionProduct={onRemoveAccordionProduct}
              />
            ))}
          </Accordion>
        ))
      ) : (
        <>
          {accordions.map((accordion) => (
            <CategoryAccordion
              key={accordion.key}
              overrideId={overrideId}
              selectProductKey={selectProductKey}
              accordion={accordion}
              onSelectAccordionProduct={onSelectAccordionProduct}
              onReorderAccordionProduct={onReorderAccordionProduct}
              onAddProductClick={handleOpenAddProductModal}
              onReplaceAccordionProduct={onReplaceAccordionProduct}
              onOpenReplaceModal={handleOpenReplaceModal}
              onRemoveAccordionProduct={onRemoveAccordionProduct}
            />
          ))}
        </>
      )}
      {isAddProductModalOpen && (
        <ProductListModalV2
          open={isAddProductModalOpen}
          onClose={handleCloseAddProductModal}
          onSelect={handleAddProductSelect}
          heading={lang.addProducts}
          filters={productListModalFilters}
        />
      )}
      {isReplaceModalOpen && (
        <ProductListModalV2
          open={isReplaceModalOpen}
          onClose={handleCloseReplaceModal}
          onSelect={handleReplaceProductSelect}
          heading={lang.replaceProduct}
          filters={productListModalFilters}
          singleSelectionMode
        />
      )}
    </ProductLineupAccordionWrapper>
  );
};
